import t from "@repo/localization/string.macro";
import { CreditFactor as CanadaCreditFactorPb } from "@repo/protobuf/gen/kikoff_canada/protobuf/models/credit/credit_factor_pb";
import Protobuf from "@repo/protobuf/utils";
import Case from "@repo/utils/Case";
import Duration, { msIn } from "@repo/utils/Duration";
import Enum from "@repo/utils/Enum";
import Interval from "@repo/utils/Interval";
import UFunction from "@repo/utils/UFunction";

import ReportAccount, { ReportAccountCanada } from "./ReportAccount.js";

type CreditFactor = {
  name: string;
  type: CreditFactor.Type;
  grade: CreditFactor.Grade;
  impact: CreditFactor.Impact;
  message: string;
  factorDiff: number;
  value: number;
  factorDiffTimeDeltaDays: number;
  details:
    | CreditFactor.CardUsage
    | CreditFactor.PaymentHistory
    | CreditFactor.CreditMix
    | CreditFactor.DerogatoryMarks
    | CreditFactor.CreditAge
    | CreditFactor.HardInquiries;
};

namespace CreditFactor {
  export type Grade = "na" | "a" | "b" | "c" | "d";

  export type Impact = "low" | "medium" | "high";
  export namespace Impact {
    export const format = (() => {
      const map = {
        low: () => t`Low`,
        medium: () => t`Medium`,
        high: () => t`High`,
      } as Record<Impact, () => string>;
      return (impact: Impact) => map[impact]();
    })();
  }

  export const types = [
    "cardUsage",
    "paymentHistory",
    "creditMix",
    "derogatoryMarks",
    "creditAge",
    "hardInquiries",
  ] as const;
  export type Type = (typeof types)[number];
  export type ByType = {
    cardUsage: CreditFactor.CardUsage;
    paymentHistory: CreditFactor.PaymentHistory;
    creditMix: CreditFactor.CreditMix;
    derogatoryMarks: CreditFactor.DerogatoryMarks;
    creditAge: CreditFactor.CreditAge;
    hardInquiries: CreditFactor.HardInquiries;
  };

  export const attrByType: Record<
    Type,
    { format(value: number): string | number; inverse: boolean }
  > = {
    cardUsage: {
      format: (value: number) => `${value}%`,
      inverse: true,
    },
    paymentHistory: {
      format: (value: number) => `${value} on-time`,
      inverse: false,
    },
    creditMix: {
      format: UFunction.identity,
      inverse: false,
    },
    derogatoryMarks: {
      format: UFunction.identity,
      inverse: true,
    },
    creditAge: {
      format: (value: number) => Duration.format(value * msIn.month, "short"),
      inverse: false,
    },
    hardInquiries: {
      format: UFunction.identity,
      inverse: true,
    },
  };

  export type CardUsage = {
    kikoffUtilization: CardUsage.Utilization;
    totalUtilization: CardUsage.Utilization;
    accounts: ReportAccount.Preview[];
  };
  export namespace CardUsage {
    export type Utilization = {
      name: string;
      balance: number;
      limit: number;
    };
    export namespace Utilization {
      export const tier = Interval.Map.createLookup({
        "[0, 10)": { color: "var(--rating-a)", label: t`Excellent` },
        "[10, 30)": { color: "var(--rating-b)", label: t`Good` },
        "[30, 50)": { color: "var(--rating-c)", label: t`Fair` },
        "[50, 75)": { color: "var(--rating-d)", label: t`Poor` },
        "[75": { color: "var(--error)", label: t`Very Poor` },
      });
    }
  }

  export type PaymentHistory = {
    accounts: ReportAccount.Preview[];
  };

  export type CreditMix = {
    accounts: ReportAccount.Preview[];
  };

  export type DerogatoryMarks = {
    collectionsCount: number;
    publicRecordsCount: number;
    accounts: ReportAccount.Preview[];
    collections: DerogatoryMarks.Collection[];
    judgements: DerogatoryMarks.Judgements[];
  };

  export namespace DerogatoryMarks {
    export type Collection = {
      creditor: string;
      currentBalanceCents: number;
    };

    export type Judgements = {
      plaintiff: string;
      amountCents: number;
    };
  }

  export type CreditAge = {
    oldestAgeMonths: number;
    averageAgeMonths: number;
    accounts: ReportAccount.Preview[];
  };

  export type HardInquiries = {
    hardInquiriesCount: number;
    inquiries: HardInquiries.Inquiry[];
  };

  export namespace HardInquiries {
    export type Inquiry = {
      contactName: string;
      date: Date;
    };
  }
}

export default CreditFactor;

export namespace CreditFactorCanada {
  export const normalize = (proto: CanadaCreditFactorPb): CreditFactor => {
    const grade = Case.fromConstant(
      Enum.keyOf(CanadaCreditFactorPb.Grade, proto.grade)
    ).toCamel();

    const impact = Case.fromConstant(
      Enum.keyOf(CanadaCreditFactorPb.Impact, proto.impact)
    ).toCamel();

    return {
      ...proto,
      grade,
      impact,
      details: Details.normalize(proto.details),
      type: Details.type(proto.details),
    };
  };

  export namespace Details {
    export const normalize = (
      proto: CanadaCreditFactorPb["details"]
    ): CreditFactor["details"] => {
      if (!proto.case)
        throw new Error("Unexpected UNKNOWN credit factor type.");

      if ("inquiries" in proto.value)
        return {
          ...proto.value,
          inquiries: proto.value.inquiries.map((inquiry) => ({
            ...inquiry,
            date: Protobuf.Timestamp.toDate(inquiry.date!),
          })),
        };

      if ("accounts" in proto.value)
        return {
          ...proto.value,
          accounts: proto.value.accounts.map(
            ReportAccountCanada.Preview.normalize
          ),
        };

      return proto.value;
    };

    export const type = (
      proto: CanadaCreditFactorPb["details"]
    ): CreditFactor.Type => {
      if (proto.case == undefined) {
        throw new Error("Unexpected UNKNOWN credit factor type.");
      }
      return proto.case;
    };
  }
}
